import { useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, Moon, Heart } from "lucide-react";

const HERO_BG =
  "https://images.unsplash.com/photo-1492648841336-8e8503376b73?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwyfHxjZWxlc3RpYWwlMjBuaWdodCUyMHNreSUyMHN0YXJzJTIwZ29sZHxlbnwwfHx8fDE3ODE5ODk5MDB8MA&ixlib=rb-4.1.0&q=85";

export default function Hero({ onSubmit, loading }) {
  const [name, setName] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [birthTime, setBirthTime] = useState("");
  const [error, setError] = useState("");

  const submit = (e) => {
    e.preventDefault();
    if (!name.trim() || !birthDate) {
      setError("Bitte gib deinen Namen und dein Geburtsdatum ein.");
      return;
    }
    setError("");
    onSubmit({ name: name.trim(), birth_date: birthDate, birth_time: birthTime || null });
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center px-4 sm:px-6 py-16 overflow-hidden">
      <div className="absolute inset-0 z-0">
        <img src={HERO_BG} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#090514]/85" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#090514] via-transparent to-[#090514]" />
      </div>

      <div className="relative z-10 w-full max-w-3xl mx-auto text-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: "easeOut" }}
        >
          <div className="inline-flex items-center gap-2 mb-6 px-4 py-1.5 rounded-full glass gold-border">
            <Sparkles className="w-3.5 h-3.5" style={{ color: "var(--gold)" }} />
            <span className="text-xs uppercase tracking-[0.28em]" style={{ color: "var(--gold)" }}>
              Lenormand &amp; Tarot
            </span>
          </div>

          <h1 className="font-display font-light italic text-5xl md:text-6xl lg:text-7xl leading-none tracking-tight mb-2 text-glow-gold">
            Single‑Seelenanalyse
          </h1>
          <p className="font-display text-2xl md:text-3xl text-white/80 mb-8">
            Dein nächster Partner
          </p>

          <p className="text-base md:text-lg leading-relaxed text-white/70 max-w-2xl mx-auto mb-10">
            Willkommen in deiner Single‑Seelenanalyse. Diese Analyse zeigt dir detailliert, klar und tief,
            welche Person als Nächstes in dein Leben tritt – mit Aussehen, Charakter, Beruf, Bedürfnissen,
            Wünschen, Beziehungsstil, Erkennungsmerkmalen, Zeitqualität und Ort des Kennenlernens.
          </p>
        </motion.div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.25, ease: "easeOut" }}
          className="glass gold-border rounded-2xl p-6 sm:p-8 text-left max-w-xl mx-auto"
          data-testid="analysis-form"
        >
          <div className="flex items-center gap-2 mb-6 justify-center">
            <Moon className="w-5 h-5" style={{ color: "var(--gold)" }} />
            <span className="font-display text-xl text-white/90">Deine Angaben</span>
          </div>

          <div className="space-y-5">
            <div>
              <label className="block text-xs uppercase tracking-[0.18em] text-white/50 mb-2">Dein Name</label>
              <input
                data-testid="input-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="z. B. Anna"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 outline-none focus:border-[#D4AF37]/60 focus:ring-1 focus:ring-[#D4AF37]/30 transition"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs uppercase tracking-[0.18em] text-white/50 mb-2">Geburtsdatum</label>
                <input
                  data-testid="input-birthdate"
                  type="date"
                  value={birthDate}
                  onChange={(e) => setBirthDate(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white outline-none focus:border-[#D4AF37]/60 focus:ring-1 focus:ring-[#D4AF37]/30 transition [color-scheme:dark]"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.18em] text-white/50 mb-2">
                  Uhrzeit <span className="normal-case tracking-normal text-white/30">(optional)</span>
                </label>
                <input
                  data-testid="input-birthtime"
                  type="time"
                  value={birthTime}
                  onChange={(e) => setBirthTime(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white outline-none focus:border-[#D4AF37]/60 focus:ring-1 focus:ring-[#D4AF37]/30 transition [color-scheme:dark]"
                />
              </div>
            </div>

            {error && (
              <p data-testid="form-error" className="text-sm text-rose-300/90">{error}</p>
            )}

            <div className="relative pt-2">
              <div className="absolute inset-x-8 -inset-y-1 rounded-full bg-[#D4AF37]/25 blur-xl animate-pulse-glow pointer-events-none" />
              <button
                data-testid="start-analysis-button"
                type="submit"
                disabled={loading}
                className="relative w-full flex items-center justify-center gap-2 rounded-full border border-[#D4AF37] text-[#D4AF37] font-medium tracking-wide py-3.5 hover:bg-[#D4AF37] hover:text-[#090514] transition-all duration-300 disabled:opacity-60"
              >
                <Heart className="w-4 h-4" />
                {loading ? "Die Karten werden gemischt…" : "Meine Seelenanalyse beginnen"}
              </button>
            </div>
          </div>
        </motion.form>

        <p className="mt-6 text-xs text-white/40 tracking-wide">
          Kostenlos · Basierend auf Lenormand‑ &amp; Tarotkarten
        </p>
      </div>
    </section>
  );
}
